import {
  type SupportedLanguage,
  SUPPORTED_LANGUAGES,
  SUPPORTED_LANGUAGES_SET,
} from '@listening/api-contract/schemas';
import { Predicate } from 'effect';

export {
  type SupportedLanguage as SupportedTextToAudioLanguage,
  SUPPORTED_LANGUAGES as SUPPORTED_TEXT_TO_AUDIO_LANGUAGES,
} from '@listening/api-contract/schemas';

export const LANG_DETECT_MIN_CHARS = 100;

export const EXTRA_LANGUAGE_DETECTION_SUBSET = [
  'uk',
  'ru',
  'ar',
  'ko',
  'de',
  'nl',
  'pl',
  'tr',
  'vi',
  'th',
  'id',
  'sv',
  'da',
  'fi',
  'no',
  'cs',
  'ro',
  'hu',
  'el',
  'he',
  'bg',
  'hr',
  'sk',
  'sl',
  'lt',
  'lv',
  'et',
  'ka',
  'mk',
  'sq',
  'ms',
  'tl',
  'sw',
  'la',
] as const;

export type LanguageSupport =
  | { status: 'supported'; lang: SupportedLanguage }
  | { status: 'unsupported'; lang: string };

export function normalizeDetectedLanguage(lang: string) {
  return lang === 'la' ? 'en' : lang;
}

export function getLanguageSupport(lang: string): LanguageSupport {
  const normalized = normalizeDetectedLanguage(lang);
  if (SUPPORTED_LANGUAGES_SET.has(normalized as SupportedLanguage)) {
    return {
      status: 'supported',
      lang: normalized as SupportedLanguage,
    };
  }

  return { status: 'unsupported', lang: normalized };
}

export function getLanguageName(lang: string, locale = 'en') {
  const displayNames = Predicate.isFunction(Intl.DisplayNames)
    ? new Intl.DisplayNames([locale], { type: 'language' })
    : null;

  return displayNames?.of(lang) ?? lang;
}

export function getSupportedLanguageNames(locale = 'en') {
  return SUPPORTED_LANGUAGES.map((lang) => getLanguageName(lang, locale));
}

const EMOJI_REGEX = /\p{Emoji_Presentation}|\p{Emoji}\uFE0F/u;

export type UnsupportedTtsTextInput =
  | { kind: 'emoji' }
  | { kind: 'control-character' };

function hasUnsupportedControlCharacter(text: string) {
  for (let index = 0; index < text.length; index++) {
    const code = text.codePointAt(index);
    if (code === undefined) continue;
    if (
      code === 0x7f ||
      (code <= 0x1f && code !== 0x09 && code !== 0x0a && code !== 0x0d)
    ) {
      return true;
    }
  }
  return false;
}

export function findUnsupportedTtsTextInput(
  text: string,
): UnsupportedTtsTextInput | null {
  if (EMOJI_REGEX.test(text)) return { kind: 'emoji' };
  if (hasUnsupportedControlCharacter(text)) {
    return { kind: 'control-character' };
  }
  return null;
}

export function describeUnsupportedTtsTextInput(
  issue: UnsupportedTtsTextInput,
) {
  switch (issue.kind) {
    case 'emoji': {
      return 'Emoji and pictographic symbols are not supported in text-to-audio input yet. Remove them and try again.';
    }
    case 'control-character': {
      return 'This text contains hidden control characters that are not supported for audio conversion. Remove them and try again.';
    }
    default: {
      issue satisfies never;
      return 'This text contains characters that are not supported for audio conversion.';
    }
  }
}
